import { DateTime } from 'luxon';

enum Objektstatus {
  Active = 0,
  MasterdataDeleted = 10,
}

enum BeteiligtePersonType {
  Auftraggeber = 10,
  AusschreibendeStelle = 20,
  Planung = 30,
  Bauleitung = 40,
  Sachverstaendiger = 50,
  Behoerde = 60,
  Partner = 70,
  Intern = 80,
  Sonstige = 90,
  Nachunternehmer = 100,
}

enum ReportType {
  Announcement = 10,
  Award = 20,
}

enum ReportTemplateScope {
  FirstPage = 10,
  SecondPageAndFollowingPages = 20,
}

enum ReportTemplateSection {
  Header = 10,
  Footer = 20,
}

enum Speicherstatus {
  Ausstehend = 0,
  Speichert = 1,
  Gespeichert = 2,
}

enum AnhangReferenzQuelle {
  Projekt = 10,
  Vergabeeinheit = 20,
  Bieterangebot = 30,
}

enum AnhangKategorie {
  Ausschreibungsunterlagen = 0,
  Sonstiges = 5,
  Planunterlagen = 10,
  GesetzlicheVorschriften = 20,
  Vertragsbedingungen = 30,
  TitelbildAusschreibungen = 40,
  Materialliste = 50,
}

enum CertificateTypes {
  ClearanceCertificates = 10,
  RegisterExtracts = 20,
  TaxCertificates = 30,
  Insurances = 40,
  AdditionalQualifications = 50,
  PrequalificationAndSpecialistCertificates = 60,
  IsoCertification = 70,
  Others = 80,
}

enum AnredenType {
  Herr = 10,
  Frau = 20,
  Firma = 30,
  Neutral = 40,
}

// ===== BASIC S =====

interface Address {
  bezeichnung?: string;
  strasse?: string;
  land?: string;
  plz?: string;
  ort?: string;
}

interface Communication {
  telefon?: string;
  fax?: string;
  mobil?: string;
  eMail?: string;
  internet?: string;
}

interface CostCenter {
  kostenstelleId?: string;
  bibliothekId?: string;
  nummer?: string;
  kurzname?: string;
  bezeichnung?: string;
  beginn?: DateTime;
  ende?: DateTime;
  status?: Objektstatus;
}

// ===== EMPLOYEE S =====
interface OrganisationEmployeeFunction {
  organisationEmployeeFunctionId?: string;
  organisationEmployeeId?: string;
  personRolesTypeId?: string;
  function?: PersonRoleType;
}

interface PersonRoleType {
  beschreibung?: string;
}

interface OrganisationEmployeeCommunication {
  organisationMitarbeiterKommunikationId?: string;
  organisationMitarbeiterId?: string;
  kommunikationstypId?: string;
  istHauptkommunikation?: boolean;
  verbindung?: string;
  status?: Objektstatus;
  istAusStamm?: boolean;
  kommunikationstyp?: CommunicationType;
}

interface CommunicationType {
  kommunikationstypId?: string;
  lizenznehmerId?: string;
  name?: string;
  status?: Objektstatus;
}

interface OrganisationEmployee {
  organisationMitarbeiterId?: string;
  organisationId?: string;
  bibliothekId?: string;
  anrede?: AnredenType;
  titel?: string;
  vorname?: string;
  weitereVornamen?: string;
  nachname?: string;
  istGeschaeftsfuehrer?: boolean;
  nummer?: string;
  bemerkung?: string;
  emailSignatur?: string;
  replyToEmailId?: string;
  status?: Objektstatus;
  kommunikation?: OrganisationEmployeeCommunication[];
  functions?: OrganisationEmployeeFunction[];
  modificationDateTime?: DateTime;
}

// ===== INVOLVED PERSON S =====
interface Salutation {
  anrede?: AnredenType;
  nachname?: string;
  vorname?: string;
}

interface InvolvedPersonContactPerson {
  ansprechpartnerId?: string;
  quellAnsprechpartnerId?: string;
  bemerkung?: string;
  funktion?: string;
  anrede?: Salutation;
  kommunikation?: Communication;
}

interface InvolvedPerson {
  beteiligtePersonId?: string;
  typ?: BeteiligtePersonType;
  kommentar?: string;
  geschaeftspartnerId?: string;
  organisationMitarbeiterNummer?: string;
  kurzBezeichnung?: string;
  bezeichnung?: string;
  anrede?: Salutation;
  anschrift?: Address;
  kommunikation?: Communication;
  ansprechpartner?: InvolvedPersonContactPerson[];
}

// ===== ORGANIZATION S =====

interface Organisation {
  organisationId?: string;
  bibliothekId?: string;
  nummer?: string;
  bezeichnung?: string;
  kurzbezeichnung?: string;
  bauvorhaben?: string;
  anschrift?: Address;
  kommunikation?: Communication;
  blobStorageLogoUrl?: string;
  logo?: string;
  status?: Objektstatus;
  modificationDateTime?: DateTime;
}

// ===== OZ DEFINITION S =====

interface OzDefinition {
  ozAufbauId?: string;
  bibliothekId?: string;
  bezeichnung?: string;
  beschreibung?: string;
  status?: Objektstatus;
  // Additional OZ structure properties would go here
}

// ===== ATTACHMENT S =====

interface ProjectAttachment {
  anhangId?: string;
  projektId?: string;
  dokumentId?: string;
  quelleId?: string;
  quelle?: AnhangReferenzQuelle;
  quelleAnhangId?: string;
  dateiname?: string;
  dateiendung?: string;
  bezeichnung?: string;
  autor?: string;
  typ?: string;
  dateigroesse?: number;
  aenderungsdatum?: DateTime;
  speicherstatus?: Speicherstatus;
  kategorie?: AnhangKategorie;
}

// ===== PROJECT STATE S =====
export interface ProjectState {
  projektstadiumId?: string;
  projektId?: string;
  nummer?: string;
  kurzname?: string;
  projektstadiumnummer?: string;
  stadiumTyp?: ProjektstadiumType;
  projektstadiumTypId?: string;
  istAusStamm?: boolean;
  status?: number;
  vatRateFull?: number;
  vatRateReduced?: number;
}

export enum ProjektstadiumType {
  Angebot = 10,
  Auftrag = 20,
  Arbeit = 30,
}

// ===== REPORT TEMPLATE S =====

interface ReportTemplate {
  reportTemplateId?: string;
  name?: string;
  url?: string;
  thumbnailUrl?: string;
  filename?: string;
  mimeType?: string;
  section?: ReportTemplateSection;
  scope?: ReportTemplateScope;
}

interface ProjectReportTemplate {
  id?: string;
  reportTemplateId?: string;
  assignedToId?: string;
  reportScope?: ReportTemplateScope;
  reportType?: ReportType;
  reportTemplate?: ReportTemplate;
}

// ===== MAIN PROJECT  =====

export interface Project {
  bibliothekId?: string;
  projektId?: string;
  organisationId?: string;
  nummer?: string;
  bezeichnung?: string;
  kostenstelle?: CostCenter;
  bauvorhaben?: string;
  projektleitung?: OrganisationEmployee;
  bauleitung?: OrganisationEmployee;
  baubeginnGeplant?: DateTime;
  bauendeGeplant?: DateTime;
  abnahmeGeplant?: DateTime;
  anschrift?: Address;
  kommunikation?: Communication;
  beteiligtePersonen?: InvolvedPerson[];
  ozAufbau?: OzDefinition;
  anhaenge?: ProjectAttachment[];
  kostenstelleId?: string;
  projektleitungId?: string;
  bauleitungId?: string;
  ozAufbauId?: string;
  organisation?: Organisation;
  waehrungsbezeichnung?: string;
  waehrungssymbol?: string;
  bezeichnungPreisanteil1?: string;
  bezeichnungPreisanteil2?: string;
  bezeichnungPreisanteil3?: string;
  bezeichnungPreisanteil4?: string;
  bezeichnungPreisanteil5?: string;
  bezeichnungPreisanteil6?: string;
  projektstadien?: ProjectState[];
  projekttypId?: string;
  istAusStamm?: boolean;
  status?: Objektstatus;
  istAngebotsfristZwingend?: boolean;
  isUseOwnReportTemplates?: boolean;
  reportTemplates?: ProjectReportTemplate[];
  angebotsfrist?: DateTime;
  distanceToOrganisationHeadquarter?: number;
  modificationDateTime?: DateTime;
}
